import { InjectionToken, Type } from '@angular/core';
import { PlaybackController } from './playback-controller';

/**
 * Pluggable toolbar registry contracts.
 *
 * The toolbar is a thin HOST that renders an ordered, per-sensor-filtered list of
 * control plugins. Whether a control appears for a sensor is pure configuration
 * (`SensorToolbarConfig`); adding a new control means registering a descriptor
 * (`TOOLBAR_CONTROLS`) — no host edits.
 */

/**
 * `track` is Voyage Radar's per-vessel track bar — a third toolbar that stacks
 * above the live bar while a vessel's path is being replayed.
 *
 * `vessel` is Voyage Radar's located-vessel bar — raised for a vessel found via
 * worldwide search (which can exist with NO snapshot on the map at all), naming
 * the hull and carrying its per-vessel actions. See VoyageVesselController.
 */
export type ToolbarSlot = 'live' | 'playback' | 'track' | 'vessel';

/**
 * What an OPERATOR can actually do — the third registry beside "what controls
 * exist" (`TOOLBAR_CONTROLS`) and "what this operator shows" (`SensorToolbarConfig`).
 *
 * Deliberately names only what VARIES between operators. Transport is not a
 * capability: it is abstract on `PlaybackController`, so every operator has it
 * by construction.
 *
 * Declared on `SensorToolbarConfig` rather than on `PlaybackController`, and
 * that placement is load-bearing: a capability is a fact about the OPERATOR,
 * but the controller a toolbar-host is driven by varies per bar — the track
 * bar runs on `VoyageTrackController` and the standalone vessel bar on
 * `VoyageVesselController`, neither of which is an operator. The config is the
 * one per-operator object every bar of that operator already carries, so it is
 * the one place the answer cannot depend on which bar happens to be asking.
 */
export type PlaybackCapability =
  | 'pull-refresh'      // user-initiated paid pull of the latest positions
  | 'clustering'        // rendering goes through the count-bubble display pass
  | 'track-replay'      // single-vessel track fetch + replay
  | 'vessel-search'     // vessel search + located-vessel machinery
  | 'priced-actions';   // per-vessel paid actions (port calls, nearby, lookup)

/** Injected into every control component; gives it the shared controller + its config. */
export interface ToolbarControlContext {
  controller: PlaybackController;
  slot: ToolbarSlot;
  options: Record<string, any>;
}

/**
 * DI token the host uses to hand each dynamically-rendered control its context.
 * (Angular 14's `NgComponentOutlet` has no input binding, so context is provided
 * via a per-control `Injector` instead of an `@Input`.)
 */
export const TOOLBAR_CONTROL_CONTEXT = new InjectionToken<ToolbarControlContext>(
  'TOOLBAR_CONTROL_CONTEXT',
);

/** Contract every control plugin component implements. */
export interface ToolbarControl {
  context: ToolbarControlContext;
}

/** Registration record for a control plugin (provided via TOOLBAR_CONTROLS multi-token). */
export interface ToolbarControlDescriptor {
  id: string;
  component: Type<ToolbarControl>;
  /** Which toolbars this control may appear in. */
  slots: ToolbarSlot[];
  defaultOrder: number;
  /**
   * The ONE operator this control belongs to, matched against
   * `SensorToolbarConfig.operatorKey`. Omitted = provider-neutral, mountable
   * by anyone — which is what every descriptor in the shared registry is.
   *
   * `TOOLBAR_CONTROLS` is one flat `multi` array across all providers, so ids
   * are global while the CONTROLS behind them are not. Every provider will
   * eventually want a control called `search`, or `track`, or `refresh`: those
   * are names for what a user is doing, not for an implementation. Without an
   * owner the host's `find` would take whichever descriptor its provider array
   * happened to be spread first in `app.module.ts` — so AISstream's config
   * would mount Voyage Radar's `SearchControlComponent`, which injects
   * `VoyageSearchService` and would fail at DI in the best case.
   *
   * Capabilities cannot do this job and are not a second way of spelling it. A
   * capability says what an operator CAN DO, and two operators that can both
   * search still need two different components; this says which component is
   * whose. Voyage Radar's controls declare both — `vessel-search` because a
   * VR task without the feature should not show the button, and this because
   * the button, when shown, must be VR's.
   */
  operatorKey?: string;
  /**
   * Capabilities the operator must declare for this control to mount.
   *
   * The host checks these against `SensorToolbarConfig.capabilities` in the
   * same pass that checks `slots`. A control whose requirement is unmet does
   * not render — loudly, in dev mode — instead of mounting over callbacks that
   * were never wired and swallowing every click. Both halves of that failure
   * have shipped: the port-call button sat on a bar with no MMSI to ask about
   * for a release, and `popupOptsFor` still gates the popup's copies of these
   * buttons on `onRequestOpen` being non-null for exactly this reason.
   *
   * Omitted = the control is pure display/transport and mounts for anyone.
   */
  requires?: PlaybackCapability[];
}

export const TOOLBAR_CONTROLS = new InjectionToken<ToolbarControlDescriptor[]>(
  'TOOLBAR_CONTROLS',
);

/** Per-control entry inside a sensor's toolbar configuration. */
export interface ToolbarControlConfig {
  id: string;
  enabled?: boolean;
  order?: number;
  options?: Record<string, any>;
}

/** Full per-sensor toolbar configuration (which controls appear in each slot). */
export interface SensorToolbarConfig {
  operatorKey: string;
  /**
   * What this operator can actually DO — checked against each descriptor's
   * `requires` at resolve time. See `PlaybackCapability` for why this lives on
   * the config rather than on `PlaybackController`. Required, not optional: a
   * new operator states its capabilities in the same place it states its bars,
   * and an empty array is an explicit "display only", never a forgotten field.
   */
  capabilities: PlaybackCapability[];
  live: ToolbarControlConfig[];
  playback: ToolbarControlConfig[];
  /** Optional per-vessel track bar (Voyage Radar); omitted by other sensors. */
  track?: ToolbarControlConfig[];
  /** Optional located-vessel bar (Voyage Radar search); omitted by other sensors. */
  vessel?: ToolbarControlConfig[];
}

export const SENSOR_TOOLBAR_CONFIGS = new InjectionToken<SensorToolbarConfig[]>(
  'SENSOR_TOOLBAR_CONFIGS',
);
